import { Button, SlideOutModal, SlideOutModalProps } from "../../../components";
import { useEffect, useRef, useState } from "react";
import { sharedProperty } from "../../../hooks";
import { BookingModel } from "../../../data/entities/booking/BookingModel";
import { ErrorMessage, FieldArray, Formik } from "formik";
import { Accordion, Form, Button as BSButton } from "react-bootstrap";
import TimeSlotPicker from "../components/TimeSlotPicker";
import { DatePicker, Popup } from "@progress/kendo-react-all";
import providerStyle from "../styles/Provider.module.scss";
import { useBookingSchedule } from "../hooks";
import { BookingCalendar } from "../../../components/BookingCalendar";
import React from "react";

export interface BookingScheduleModalProps extends SlideOutModalProps {
  onSubmit: any;
  booking: sharedProperty<BookingModel>;
  locationId: number;
  conflictError?: string;
}

export function BookingScheduleModal(
  props: Readonly<BookingScheduleModalProps>
) {
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const { modalBodyRef, validationSchema, onDateChange, onTimeSelect, isTimeSlotDisabled, availableDates, selectedTimes } = useBookingSchedule(props);
  const submitRef = useRef<HTMLButtonElement>(null);
  const ModalPopup = (props: any) => (
    <Popup {...props} appendTo={modalBodyRef.current} />
  );
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Expand first unselected accordion on mount
  useEffect(() => {
    expandFirstUnselected();
  }, [])

  // Re-expand when booking events change (e.g., after date change)
  useEffect(() => {
    if (props.showState.value) {
      expandFirstUnselected();
    }
  }, [props.booking.value.bookingEvents, props.showState.value]);


  const getDisplayTime = (index: number) => {
    const selectedTime = Object.values(selectedTimes).find(t => t.index === index);
    
    if (!selectedTime) return "--:-- --";
    
    const dateObj = selectedTime.startTime instanceof Date 
      ? selectedTime.startTime 
      : new Date(selectedTime.startTime);
    
    // Check if valid date
    if (isNaN(dateObj.getTime())) {
      return "--:-- --";
    }
    
    return dateObj
      .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      .toUpperCase();
  };

  const expandFirstUnselected = () => {
    const firstUnselectedIndex = props.booking.value.bookingEvents.findIndex(
      (event) => !event.startTime
    );
    if (firstUnselectedIndex !== -1) {
      setActiveKey(`event-${firstUnselectedIndex}`);
    } else {
      setActiveKey(null); // Collapse all if all have a time
    }
  };

  return (
    <SlideOutModal
      {...props}
      size="lg"
      title={"Your Booking"}
      modalBodyRef={modalBodyRef}
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button
                onClick={() => props.showState.setter(false)}
                variant="cancel"
                className="w-100"
              >
                Back
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button
                onClick={() => submitRef.current?.click()}
                variant="primary"
                className="w-100"
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Formik
        enableReinitialize={true}
        validationSchema={validationSchema}
        initialValues={{
          date: props.booking.value.date || props.booking.value.bookingEvents[0]?.date || null,
          events: props.booking.value.bookingEvents.map((event) => ({
            id: 0,
            startTime: event.startTime || null,
            endTime: event.endTime || null,
          })),
        }}
        onSubmit={(values) => {
          props.booking.setter({
            ...props.booking.value,
            date: values.date,
            bookingEvents: props.booking.value.bookingEvents.map(
              (event, index) => ({
                ...event,
                date: values.date,
                startTime: values.events[index].startTime,
                endTime: values.events[index].endTime,
                // Ensure any-provider events always send -1 to the backend 
                providerId: event.isAnyProvider ? -1 : event.providerId,
              })
            ),
          });
          props.onSubmit(props.booking.value);
        }}
      >
        {({handleSubmit, setFieldValue, setTouched, setErrors, touched, values, errors,}) => (
          <Form onSubmit={handleSubmit}>
            {props.conflictError && (
              <div className="alert alert-danger mb-3" role="alert">
                <i className="fa fa-exclamation-circle me-2" />
                {props.conflictError}
              </div>
            )}

            {/* Date Picker */}
            <div className="mb-4">
              <h5 className="text-muted mb-3 fw-semibold" style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Select Your Date
              </h5>
              <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <i className="fa fa-calendar me-3 text-muted"></i>
                  <DatePicker
                    show={isDatePickerOpen}
                    name="date"
                    popup={ModalPopup}
                    value={values.date}
                    valid={errors.date ? false : true}
                    min={new Date()}
                    onOpen={() => setIsDatePickerOpen(true)}
                    onClose={() => setIsDatePickerOpen(false)}
                    calendar={() => (
                      <BookingCalendar
                        availableDates={availableDates!}
                        value={values.date!}
                        onChange={(event: any) => {
                          onDateChange(event, setFieldValue, setTouched, setErrors);
                          setIsDatePickerOpen(false);
                          // Expand first unselected after date change
                          setTimeout(() => {
                            expandFirstUnselected();
                          }, 0);
                        }}
                      />
                    )}
                  />
                </div>
                {errors.date && (
                  <div className="text-danger mt-2 small">
                    {errors.date}
                  </div>
                )}
              </div>
            </div>

            {/* Time Slots */}
            <div className="mb-3">
              <h5 className="text-muted mb-3 fw-semibold" style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Select Time Slots
              </h5>
              <FieldArray
                name="events"
                render={(arrayHelpers) => (
                  <div className="d-flex flex-column gap-2">
                    {props.booking.value.bookingEvents.map((event, index) => (
                      <React.Fragment key={event.id ?? index}>
                        <Accordion
                          className={`${
                            touched.events &&
                            errors.events &&
                            errors.events[index] &&
                            (errors.events[index] as any).startTime
                              ? providerStyle.invalidAccordion
                              : ""
                          }`}
                          defaultActiveKey=""
                          activeKey={activeKey === `event-${index}` ? "0" : null}
                          onSelect={() => {
                            setActiveKey((prev) => (prev === `event-${index}` ? null : `event-${index}`));
                          }}
                        >
                          <Accordion.Item eventKey="0">
                            <Accordion.Header>
                              <div className="w-100">
                                {/* Desktop Layout */}
                                <div className="d-none d-md-flex align-items-center w-100">
                                  {/* Time */}
                                  <div className="fw-bold" style={{ fontSize: "1.25rem", minWidth: "90px" }}>
                                    {getDisplayTime(index)}
                                  </div>
                                  {/* Divider */}
                                  <div className="mx-3 text-muted" style={{ fontSize: "1.25rem", fontWeight: "300" }}>|</div>
                                  {/* Service name */}
                                  <div className="flex-grow-1">
                                    <div className="fw-medium" style={{ fontSize: "1rem" }}>
                                      {event.service!.name}
                                    </div>
                                    <small className="text-muted" style={{ fontSize: "0.85rem" }}>
                                      {event.service!.defaultDuration} minutes
                                    </small>
                                  </div>
                                  {/* Provider image + name */}
                                  {!event.service?.hideProviders && (
                                    <div className="d-flex align-items-center ms-3 me-3">
                                      {event.isAnyProvider ? (
                                        <div 
                                          className={`${providerStyle.providerImage} d-flex align-items-center justify-content-center rounded-circle flex-shrink-0`} 
                                          style={{ backgroundColor: '#f7bc9e' }}
                                        >
                                          <i className="fa fa-users text-white" style={{ fontSize: '0.9rem' }} />
                                        </div>
                                      ) : (
                                        <img
                                          src={`${apiBaseUrl}provider/${event.provider?.id}/profilepicture`}
                                          alt={event.provider?.name}
                                          className={providerStyle.providerImage}
                                        />
                                      )}
                                      <div className="ms-2 fw-medium" style={{ fontSize: "0.9rem" }}>
                                        {event.isAnyProvider ? 'Any Provider' : event.provider?.name}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Mobile Layout */}
                                <div className="d-md-none w-100">
                                  <div className="fw-semibold" style={{ fontSize: "1.05rem", color: '#2f4157' }}>
                                    {event.service!.name}
                                  </div>

                                  <div className="d-flex align-items-center mt-1 gap-2">
                                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                                      <i className="fa fa-clock me-1"></i>
                                      {event.service!.defaultDuration} min
                                    </span>
                                    {getDisplayTime(index) !== "--:-- --" ? (
                                      <>
                                        <span className="text-muted">•</span>
                                        <span className="fw-bold text-muted" style={{ fontSize: "0.85rem" }}>
                                          {getDisplayTime(index)}
                                        </span>
                                      </>
                                    ) : (
                                      <span style={{ fontSize: "0.85rem", color: '#e67e22', fontWeight: 500 }}>
                                        — Tap to select time
                                      </span>
                                    )}
                                  </div>

                                  {!event.service?.hideProviders && (
                                    <div className="d-flex align-items-center mt-2 pt-2" style={{ borderTop: '1px solid #eee' }}>
                                      {event.isAnyProvider ? (
                                        <div
                                          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                          style={{ width: '28px', height: '28px', backgroundColor: '#f7bc9e' }}
                                        >
                                          <i className="fa fa-users text-white" style={{ fontSize: '0.7rem' }} />
                                        </div>
                                      ) : (
                                        <img
                                          src={`${apiBaseUrl}provider/${event.provider?.id}/profilepicture`}
                                          alt={event.provider?.name}
                                          className={providerStyle.providerImage}
                                          style={{ width: '28px', height: '28px' }}
                                        />
                                      )}
                                      <div className="ms-2" style={{ fontSize: "0.85rem", color: '#577c8e' }}>
                                        {event.isAnyProvider ? 'Any Provider' : event.provider?.name}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Accordion.Header>
                            <Accordion.Body className="pt-3">
                              <TimeSlotPicker
                                bookingEvent={event}
                                providerId={event.isAnyProvider ? null : event.providerId}
                                serviceId={event.serviceId!}
                                locationId={props.locationId}
                                date={values.date!}
                                onTimeSelect={(time, timeSlot) => {
                                  onTimeSelect(
                                    time,
                                    event.id,
                                    index,
                                    setFieldValue,
                                    timeSlot
                                  );
                                  expandFirstUnselected();
                                }}
                                isTimeSlotDisabled={(timeSlot) =>
                                  isTimeSlotDisabled(timeSlot, event.id, event.serviceId, event.providerId, index)
                                }
                              />
                            </Accordion.Body>
                          </Accordion.Item>
                        </Accordion>
                        {errors.events && errors.events[index] && (errors.events[index] as any).startTime && (
                          <ErrorMessage
                            name={`events[${index}].startTime`}
                            component="div"
                            className={`${providerStyle.errorMessage} ms-2`}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              />
            </div>

            <BSButton ref={submitRef} type="submit" className="d-none" />
          </Form>
        )}
      </Formik>
    </SlideOutModal>
  );
}

export default BookingScheduleModal;